import type { Dropset, LogSet } from "./schemas/logSet.js";
import type { Session } from "./schemas/session.js";
import {
  EQUIPMENT_INSTANCE_NS,
  LOCATION_NS,
  readTagValue,
} from "./schemas/tag.js";
import type {
  Workout,
  WorkoutEntryGroup,
  WorkoutExerciseEntry,
  WorkoutSet,
} from "./schemas/workout.js";

// (Session, LogSet[]) → Workout. Pure function shared between the API
// facade and the web client.
//
// Group sets by (exerciseSlug, entryIdx) in first-appearance order;
// entryIdx comes from the `entry:<n>` system tag the splitter stamps
// on every set. Sets without an entry tag (legacy rows pre-S6) fall
// back to slug-only grouping for back-compat — they merge with the
// first existing entry for that slug, matching the old behaviour.
//
// Per-set kind + payload fan back out into the discriminated
// WorkoutSet shape. Tag namespaces unwrap:
//   - `superset:<id>` on any set in a group → entry.group
//   - `tag:<slug>`    → entry.tags (catalog-tag snapshot)
//   - `entry:<n>`     → entry-position discriminator (S6, 0.66.0)
//   - `equipment-instance:<slug>` → entry.equipmentInstance (EQ-1)
//   - `location:<slug>`           → entry.location (EQ-1)
//   - `note`          on the header set (entryHeaderSet) → entry.notes
//
// The function is the read-side counterpart of the write-side splitter
// inside the workouts facade. When the facade is dropped (PR 7), the
// web is the only consumer and this stays in shared as the bridge for
// any consumer that still wants the workout-centric view.
/** The `entry:<n>` position discriminator the splitter stamps, or null for a
 *  legacy (pre-S6) row that carries none.
 *
 *  ⭐ Exported since LG-71: the per-exercise index door
 *  (`GET /api/sets?exercise=`) hands back the SAME rows this function reads,
 *  and grouping them by session alone would merge an exercise performed twice
 *  in one workout into a single six-set entry. Whoever regroups those rows
 *  needs the same discriminator, and a second parser of the same tag is a
 *  second opinion about what a workout's shape was. */
export const readEntryIdxTag = (set: LogSet): number | null => {
  for (const t of set.tags ?? []) {
    if (!t.startsWith("entry:")) continue;
    const raw = t.slice("entry:".length);
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
};

/** The member that carries its entry's header: the `tag:` snapshot and the
 *  entry note. The splitter writes them on the earliest performed set, the
 *  rule read here. Rows written before #38 have them on the first set in
 *  draft order, a later row when sets were completed out of order: the one
 *  with a `tag:` is the header; without one, the earliest performed.
 *  @param members One entry's sets, sorted by performedAt. */
export const entryHeaderSet = (members: readonly LogSet[]): LogSet | undefined =>
  members.find((m) => m.tags?.some((t) => t.startsWith("tag:"))) ?? members[0];

/** A stored set's completion stamp: its own `completedAt`, else `performedAt`.
 *  A saved set is a done set; without this a correction drops it and the server deletes it (#328). */
export const storedSetCompletedAt = (s: {
  completedAt?: string | undefined;
  performedAt: string;
}): string => s.completedAt ?? s.performedAt;

/** One stored `LogSet` fanned back out into the discriminated `WorkoutSet`
 *  shape, kind by kind.
 *
 *  ⭐ Exported since LG-71, and NOT copied. The per-exercise index door
 *  (`GET /api/sets?exercise=`) answers with exactly these rows for an exercise
 *  the rolling session window is too short to see, and the logger's prefill
 *  needs them in workout shape to reach `draftSetsFromHistoryEntry` — the same
 *  function the window path already goes through. A second fan-out would be a
 *  second opinion about what a stored set MEANS: the day `amrap` or
 *  `loadedSides` changes here, the copy would keep answering the old way and
 *  the two prefills would quietly disagree about the same past set.
 *
 *  @param includeNote Whether this set's own note travels with it. False for
 *   the entry's FIRST set, whose note doubles as `entry.notes` — printing it
 *   in both places is one string shown twice.
 */
export function workoutSetFromLogSet(
  m: LogSet,
  { includeNote }: { includeNote: boolean },
): WorkoutSet {
  const noteField = includeNote && m.note ? { note: m.note } : {};
  if (m.kind === "weighted") {
    return {
      type: "weighted",
      reps: m.reps ?? 0,
      weight: m.weight ?? 0,
      ...(m.loadedSides !== undefined
        ? { loadedSides: m.loadedSides }
        : {}),
      ...(m.weightSource !== undefined
        ? { weightSource: m.weightSource }
        : {}),
      // `=== true` (not `!== undefined`) on purpose: `amrap` is
      // omit-the-default in both directions, so a stored `false`
      // never re-enters the draft as a written field. Reading it
      // back here is load-bearing — editing a saved session goes
      // assemble → hydrate → re-save, and dropping it here would
      // silently erase an AMRAP written yesterday.
      ...(m.amrap === true ? { amrap: true } : {}),
      ...(m.calibration === true ? { calibration: true } : {}),
      ...(m.rpe !== undefined ? { rpe: m.rpe } : {}),
      ...(m.rir !== undefined ? { rir: m.rir } : {}),
      ...(m.duration !== undefined ? { duration: m.duration } : {}),
      ...(m.side !== undefined ? { side: m.side } : {}),
      ...(m.fail !== undefined ? { fail: m.fail } : {}),
      ...(m.dropset ? { dropset: m.dropset as Dropset } : {}),
      completedAt: storedSetCompletedAt(m),
      ...noteField,
    };
  }
  if (m.kind === "bodyweight") {
    return {
      type: "bodyweight",
      reps: m.reps ?? 0,
      ...(m.extra !== undefined ? { extra: m.extra } : {}),
      ...(m.weightSource !== undefined
        ? { weightSource: m.weightSource }
        : {}),
      ...(m.amrap === true ? { amrap: true } : {}),
      ...(m.rpe !== undefined ? { rpe: m.rpe } : {}),
      ...(m.rir !== undefined ? { rir: m.rir } : {}),
      ...(m.duration !== undefined ? { duration: m.duration } : {}),
      ...(m.side !== undefined ? { side: m.side } : {}),
      ...(m.fail !== undefined ? { fail: m.fail } : {}),
      ...(m.dropset ? { dropset: m.dropset as Dropset } : {}),
      completedAt: storedSetCompletedAt(m),
      ...noteField,
    };
  }
  if (m.kind === "p1rm") {
    return {
      type: "p1rm",
      reps: m.reps ?? 0,
      p1rm: m.p1rm ?? 0,
      rfl: m.rfl ?? false,
      ...(m.rpe !== undefined ? { rpe: m.rpe } : {}),
      ...(m.rir !== undefined ? { rir: m.rir } : {}),
      ...(m.duration !== undefined ? { duration: m.duration } : {}),
      ...(m.side !== undefined ? { side: m.side } : {}),
      ...(m.fail !== undefined ? { fail: m.fail } : {}),
      completedAt: storedSetCompletedAt(m),
      ...noteField,
    };
  }
  if (m.kind === "cardio") {
    return {
      type: "cardio",
      duration: m.duration ?? 0,
      ...(m.distanceM !== undefined ? { distanceM: m.distanceM } : {}),
      ...(m.weight !== undefined ? { weight: m.weight } : {}),
      ...(m.avgHrBpm !== undefined ? { avgHrBpm: m.avgHrBpm } : {}),
      ...(m.calories !== undefined ? { calories: m.calories } : {}),
      ...(m.rpe !== undefined ? { rpe: m.rpe } : {}),
      ...(m.rir !== undefined ? { rir: m.rir } : {}),
      ...(m.fail !== undefined ? { fail: m.fail } : {}),
      completedAt: storedSetCompletedAt(m),
      ...noteField,
    };
  }
  if (m.kind === "timed") {
    return {
      type: "timed",
      duration: m.duration ?? 0,
      ...(m.weight !== undefined ? { weight: m.weight } : {}),
      ...(m.rpe !== undefined ? { rpe: m.rpe } : {}),
      ...(m.rir !== undefined ? { rir: m.rir } : {}),
      ...(m.fail !== undefined ? { fail: m.fail } : {}),
      completedAt: storedSetCompletedAt(m),
      ...noteField,
    };
  }
  return {
    type: "loaded-carry",
    weight: m.weight ?? 0,
    distanceM: m.distanceM ?? 0,
    ...(m.reps !== undefined ? { reps: m.reps } : {}),
    ...(m.rpe !== undefined ? { rpe: m.rpe } : {}),
    ...(m.rir !== undefined ? { rir: m.rir } : {}),
    ...(m.duration !== undefined ? { duration: m.duration } : {}),
    ...(m.side !== undefined ? { side: m.side } : {}),
    ...(m.fail !== undefined ? { fail: m.fail } : {}),
    completedAt: storedSetCompletedAt(m),
    ...noteField,
  };
}

/** A session's sets grouped into entries, keyed `${slug}#${entryIdx ?? "_legacy"}`,
 *  in the order the athlete did them: entries by first performed set, members
 *  by performedAt. assembleWorkout reorders the entries by authored index; the
 *  coach's read-only set list (#25) keeps this order. */
export function groupSetsByEntry(
  sets: readonly LogSet[],
): Map<string, LogSet[]> {
  const ordered = [...sets].sort((a, b) =>
    a.performedAt.localeCompare(b.performedAt),
  );
  // The legacy bucket collapses every untagged set for a slug into one
  // entry: the pre-S6 behaviour, kept verbatim so re-reading old workouts
  // doesn't reshape their visible structure.
  const groups = new Map<string, LogSet[]>();
  for (const s of ordered) {
    const idx = readEntryIdxTag(s);
    const key = idx === null ? `${s.exerciseSlug}#_legacy` : `${s.exerciseSlug}#${idx}`;
    const g = groups.get(key);
    if (g) g.push(s);
    else groups.set(key, [s]);
  }
  return groups;
}

export function assembleWorkout(session: Session, sets: LogSet[]): Workout {
  const groups = groupSetsByEntry(sets);
  // Entries are emitted in AUTHORED order (the `entry:<n>` index), not
  // first-performed order — the roster/card list should mirror how the
  // workout was built (warmup → compound → …), while the timeline feed
  // stays performed-ordered (Antenore 2026-06-01, inbox 11f01866 pt 1: an
  // ad-hoc exercise performed early was jumping up the roster). Legacy
  // untagged rows (`_legacy`, idx null) have no authored index, so they keep
  // their first-appearance (performed) order after the tagged entries — old
  // workouts reshape exactly as before.
  const LEGACY_SORT_BASE = 1_000_000;
  const built: { entry: WorkoutExerciseEntry; sortKey: number }[] = [];
  let entrySeq = 0;
  for (const [key, members] of groups) {
    const hashAt = key.indexOf("#");
    const slug = key.slice(0, hashAt);
    const idxPart = key.slice(hashAt + 1);
    const entryIdx = idxPart === "_legacy" ? null : Number.parseInt(idxPart, 10);
    const sortKey =
      entryIdx !== null && Number.isFinite(entryIdx)
        ? entryIdx
        : LEGACY_SORT_BASE + entrySeq;
    entrySeq++;
    const first = members[0]!;
    const header = entryHeaderSet(members)!;
    let group: WorkoutEntryGroup | undefined;
    let groupKindCircuit = false;
    for (const t of first.tags ?? []) {
      if (t.startsWith("superset:") && !group) {
        group = { id: t.slice("superset:".length), kind: "superset" };
      } else if (t === "groupkind:circuit") {
        // Sidecar tag stamped by the splitter only when the entry's
        // group.kind === "circuit". Default kind is "superset" so legacy
        // rows (no sidecar) stay supersets — the kind round-trips
        // without forcing a backfill.
        groupKindCircuit = true;
      }
    }
    if (group && groupKindCircuit) group = { ...group, kind: "circuit" };
    const setObjs: WorkoutSet[] = members.map((m) =>
      // Per-set note read-back. The header set's note doubles as
      // entry.notes (assembled below) so we omit it there to avoid
      // duplicating the same string on both surfaces. Every other set
      // exposes its own note as set-level.
      workoutSetFromLogSet(m, { includeNote: m !== header }),
    );
    const recoveredTags = Array.from(
      new Set(
        (header.tags ?? [])
          .filter((t) => t.startsWith("tag:"))
          .map((t) => t.slice("tag:".length))
          .filter((s) => s.length > 0),
      ),
    );
    // EQ-1 read-back. The splitter stamps these on every set, so reading
    // members[0] is enough and matches how `group` is recovered above.
    // Deliberately NOT merged into `recoveredTags`: they are separate
    // fields on the entry precisely so they stay out of the catalog-tag
    // snapshot that bucketTagVolumeByWeek aggregates.
    const equipmentInstance = readTagValue(first.tags, EQUIPMENT_INSTANCE_NS);
    const location = readTagValue(first.tags, LOCATION_NS);
    const entry: WorkoutExerciseEntry = {
      exerciseId: slug,
      ...(first.exerciseName ? { exerciseName: first.exerciseName } : {}),
      sets: setObjs,
      ...(header.note ? { notes: header.note } : {}),
      ...(recoveredTags.length > 0 ? { tags: recoveredTags } : {}),
      ...(equipmentInstance ? { equipmentInstance } : {}),
      ...(location ? { location } : {}),
      ...(group ? { group } : {}),
    };
    built.push({ entry, sortKey });
  }
  // Stable ascending sort by authored index (legacy entries trail, in
  // performed order). Array.prototype.sort is stable in every engine we
  // target, so equal sortKeys (can't happen for distinct entryIdx, but
  // belt-and-braces for legacy) keep insertion order.
  built.sort((a, b) => a.sortKey - b.sortKey);
  const entries: WorkoutExerciseEntry[] = built.map((b) => b.entry);
  const wk: Workout = {
    id: session.id,
    profileId: session.userSub,
    startedAt: session.startedAt,
    ...(session.endedAt ? { endedAt: session.endedAt } : {}),
    ...(session.name ? { name: session.name } : {}),
    // An assembled workout with zero entries is a legitimate signal:
    // the session row exists but no sets were ever logged (rare —
    // typically a corrupted import or a write-then-cancel). The old
    // path forged a placeholder entry to satisfy a schema constraint;
    // that's data invention. We now pass the empty array through and
    // let callers render "no sets" honestly. Workout.entries was
    // relaxed to `.min(0)` to match.
    entries,
    ...(session.fromPlanId ? { fromPlanId: session.fromPlanId } : {}),
    ...(session.instructions ? { instructions: session.instructions } : {}),
    // Energy + effort, carried READ-ONLY for the edit view (HC-8 B2c) so a
    // saved+enriched session shows its measured calories/HR instead of the
    // logger re-estimating from the draft. splitWorkout drops these on write
    // and a correction PATCHes the session in place without them (#38), so
    // the server keeps them, `externalId` included.
    ...(session.caloriesEstimate ? { caloriesEstimate: session.caloriesEstimate } : {}),
    ...(session.calories !== undefined ? { calories: session.calories } : {}),
    ...(session.avgHrBpm !== undefined ? { avgHrBpm: session.avgHrBpm } : {}),
    ...(session.peakHrBpm !== undefined ? { peakHrBpm: session.peakHrBpm } : {}),
    ...(session.externalOrigin ? { externalOrigin: session.externalOrigin } : {}),
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    ...(session.loggerUpdatedAt ? { loggerUpdatedAt: session.loggerUpdatedAt } : {}),
  };
  return wk;
}

